import { Link } from "react-router-dom";
import { MapPin, Phone, ArrowRight } from "lucide-react";
import { useSeo } from "../hooks/useSeo";
import { PageHeader, SectionLabel } from "../components/PageHeader";
import { Reveal } from "../lib/motionx";
import { LocationsMap } from "../components/LocationsMap";
import { serviceAreas, site } from "../data/site";
import { useLang } from "../i18n";

export default function ServiceAreas() {
  const { t } = useLang();
  useSeo({
    title: "Service Areas | Amenity Health Services in Houston, TX",
    description:
      "See the cities, counties, and regions served by Amenity Health Services. Outside our area? Call us — we're happy to help point you in the right direction.",
  });

  return (
    <>
      <PageHeader
        label="Service areas"
        titleLines={["Serving families", "across Houston"]}
        intro="We provide care throughout the communities listed below. If you don't see your area, please reach out — we may still be able to help or connect you with resources."
      />

      <section className="py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-8 items-start">
          <Reveal>
            <SectionLabel className="mb-6">{t("Areas we serve")}</SectionLabel>
            <ul className="grid sm:grid-cols-2 gap-3">
              {serviceAreas.map((a) => (
                <li
                  key={a}
                  data-testid={`area-${a.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="flex items-center gap-3 rounded-2xl border border-black/5 bg-white px-5 py-4"
                >
                  <MapPin size={18} className="text-teal shrink-0" />
                  <span className="text-ink/80">{t(a)}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 rounded-3xl bg-sage/20 p-7">
              <h3 className="font-heading font-semibold text-lg text-ink">{t("Outside our service area?")}</h3>
              <p className="mt-2 text-ink/65 leading-relaxed">
                {t("Give us a call — we're always happy to talk through your situation and, when we can, help point you toward the right resources.")}
              </p>
              <a
                href={site.phoneHref}
                data-testid="areas-call-btn"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-teal px-6 py-3.5 font-medium text-white hover:bg-[#2E6088] transition-colors"
              >
                <Phone size={18} /> {t("Call")} {site.phone}
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div
              className="relative z-0 rounded-[2rem] border border-black/5 bg-white overflow-hidden aspect-[4/5] shadow-sm"
            >
              <LocationsMap />
            </div>
            <div className="mt-4 space-y-2.5" data-testid="clinic-locations">
              {site.clinics.map((c) => (
                <a
                  key={c.address}
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`clinic-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="group flex items-start gap-3 rounded-2xl border border-black/5 bg-white px-5 py-3.5 hover:border-teal/40 transition-colors"
                >
                  <MapPin size={18} className="text-teal shrink-0 mt-0.5" />
                  <span>
                    <span className="block font-heading font-semibold text-ink text-[0.95rem]">{c.name}</span>
                    <span className="block text-sm text-ink/60 group-hover:text-teal transition-colors">{c.address}</span>
                  </span>
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="rounded-[2rem] bg-ink text-cream px-8 py-12 sm:px-14 flex flex-col sm:flex-row items-center justify-between gap-6">
              <h2 className="font-heading font-semibold text-2xl sm:text-3xl tracking-tight max-w-xl">
                {t("Not sure if we serve your area? Let's find out together.")}
              </h2>
              <Link
                to="/contact"
                data-testid="areas-request-btn"
                className="inline-flex items-center gap-2 rounded-full bg-cream text-ink px-7 py-4 font-medium hover:bg-peach transition-colors shrink-0"
              >
                {t("Request Information")} <ArrowRight size={18} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
